import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUserFullInfo, updateUser } from "../../api/users";
import { useNotification } from "../../hooks/useNotification";
import { UserAutocomplete, User as AutocompleteUser } from "../UserAutocomplete";

interface UserEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId: number;
    onUserUpdated: () => void;
}

interface UserEditFormData {
    name: string;
    surname: string;
    email: string;
    mobile: string;
    telegram_id: string;
    telegram_username: string;
    sex: string;
    passport: string;
    bank_account: string;
    bank_swift: string;
    bank_card: string;
    bep20: string;
    trc20: string;
    user_credit_history: string;
    inviter_id: number | null;
    inviter_name: string;
    member_fee_time: string;
    access_status: string;
    can_change_telegram: boolean;
    is_demo: boolean;
    heir_name: string;
    heir_surname: string;
    heir_passport: string;
    heir_dob: string;
}

const UserEditModal: React.FC<UserEditModalProps> = ({
    isOpen,
    onClose,
    userId,
    onUserUpdated,
}) => {
    const { t } = useTranslation("users");
    const { t: tCommon } = useTranslation("common");
    const { showNotification } = useNotification();
    const { data: userData, isLoading: isLoadingUser } = useUserFullInfo(
        isOpen ? userId : null
    );

    const formatDateTimeLocal = (value?: string | null): string => {
        if (!value) return "";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }
        return date.toISOString().slice(0, 16);
    };

    const normalizeMemberFeeTime = (value?: string): string | null => {
        if (!value) return null;
        // Добавляем секунды, если их нет (формат datetime-local без секунд)
        if (value.length === 16 && value.includes("T")) {
            return `${value}:00`;
        }
        return value;
    };

    const [formData, setFormData] = useState<UserEditFormData>({
        name: "",
        surname: "",
        email: "",
        mobile: "",
        telegram_id: "",
        telegram_username: "",
        sex: "",
        passport: "",
        bank_account: "",
        bank_swift: "",
        bank_card: "",
        bep20: "",
        trc20: "",
        user_credit_history: "0",
        inviter_id: null,
        inviter_name: "",
        member_fee_time: "",
        access_status: "0",
        can_change_telegram: false,
        is_demo: false,
        heir_name: "",
        heir_surname: "",
        heir_passport: "",
        heir_dob: "",
    });

    const [isSaving, setIsSaving] = useState(false);
    const [inviterInput, setInviterInput] = useState<string>("");

    const formatDateOnly = (value?: string | null): string => {
        if (!value) return "";
        return value.slice(0, 10);
    };

    useEffect(() => {
        if (userData && isOpen) {
            const heir = (userData as any).heir;
            setFormData({
                name: userData.name || "",
                surname: userData.surname || "",
                email: userData.email || "",
                mobile: userData.mobile || "",
                telegram_id: (userData as any).telegram_id || "",
                telegram_username: (userData as any).telegram_username || "",
                sex: userData.sex?.toString() || "",
                passport: userData.passport || "",
                bank_account: userData.bank_account || "",
                bank_swift: userData.bank_swift || "",
                bank_card: userData.bank_card || "",
                bep20: userData.bep20 || "",
                trc20: userData.trc20 || "",
                user_credit_history: (userData as any).user_credit_history?.toString() || "0",
                inviter_id: (userData as any).inviter_id ?? null,
                inviter_name: (userData as any).inviter_name ?? "",
                member_fee_time: formatDateTimeLocal((userData as any).member_fee_time),
                access_status: (userData as any).access_status?.toString() || "0",
                can_change_telegram: Boolean((userData as any).can_change_telegram ?? false),
                is_demo: Boolean((userData as any).is_demo ?? false),
                heir_name: heir?.heir_name ?? (userData as any).heir_name ?? "",
                heir_surname: heir?.heir_surname ?? (userData as any).heir_surname ?? "",
                heir_passport: heir?.heir_passport ?? (userData as any).heir_passport ?? "",
                heir_dob: formatDateOnly(heir?.heir_dob ?? (userData as any).heir_dob),
            });
            const initialInviterName =
                (userData as any).inviter_name && (userData as any).inviter_id
                    ? `${(userData as any).inviter_name} (ID: ${
                          (userData as any).inviter_id
                      })`
                    : "";
            setInviterInput(initialInviterName);
        }
    }, [userData, isOpen]);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            // Проверяем, есть ли пользователь в структуре
            const isInStructure = userData &&
                (userData as any).user_tree?.path &&
                Array.isArray((userData as any).user_tree.path) &&
                (userData as any).user_tree.path.length > 0;

            const normalizedTelegramUsername = (formData.telegram_username || "")
                .trim()
                .replace(/^@+/, "");

            const updateData: any = {
                name: formData.name,
                surname: formData.surname,
                email: formData.email,
                mobile: formData.mobile,
                telegram_id: formData.telegram_id.trim() || null,
                telegram_username: normalizedTelegramUsername || null,
                sex: formData.sex ? parseInt(formData.sex) : null,
                passport: formData.passport || null,
                bank_account: formData.bank_account || null,
                bank_swift: formData.bank_swift || null,
                bank_card: formData.bank_card || null,
                bep20: formData.bep20 || null,
                trc20: formData.trc20 || null,
                user_credit_history: parseInt(formData.user_credit_history) || 0,
                inviter_id: formData.inviter_id ?? null,
                member_fee_time: normalizeMemberFeeTime(formData.member_fee_time),
                can_change_telegram: formData.can_change_telegram,
                is_demo: formData.is_demo,
                heir_name: formData.heir_name.trim() || null,
                heir_surname: formData.heir_surname.trim() || null,
                heir_passport: formData.heir_passport.trim() || null,
                heir_dob: formData.heir_dob || null,
            };

            // Отправляем access_status только если пользователь в структуре
            if (isInStructure) {
                updateData.access_status = parseInt(formData.access_status) || 0;
            }

            await updateUser(userId, updateData);

            showNotification(t("user_updated_successfully"), "success");
            onUserUpdated();
            onClose();
        } catch (error: any) {
            const errorMessage =
                error?.response?.data?.message ||
                tCommon("error_updating_user");
            showNotification(errorMessage, "error");
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl  max-h-[80vh] overflow-y-auto mx-4 my-8">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {t("edit_user")} {userData?.name} {userData?.surname}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {isLoadingUser ? (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("name")}
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("surname")}
                                </label>
                                <input
                                    type="text"
                                    name="surname"
                                    value={formData.surname}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("email")}
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("mobile")}
                                </label>
                                <input
                                    type="text"
                                    name="mobile"
                                    value={formData.mobile}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Telegram ID
                                </label>
                                <input
                                    type="text"
                                    name="telegram_id"
                                    value={formData.telegram_id}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Telegram username
                                </label>
                                <input
                                    type="text"
                                    name="telegram_username"
                                    value={formData.telegram_username}
                                    onChange={handleInputChange}
                                    placeholder="@username"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("sex")}
                                </label>
                                <select
                                    name="sex"
                                    value={formData.sex}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="">{t("not_specified")}</option>
                                    <option value="1">{t("male")}</option>
                                    <option value="2">{t("female")}</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("passport")}
                                </label>
                                <input
                                    type="text"
                                    name="passport"
                                    value={formData.passport}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("bank_account")}
                                </label>
                                <input
                                    type="text"
                                    name="bank_account"
                                    value={formData.bank_account}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("bank_swift")}
                                </label>
                                <input
                                    type="text"
                                    name="bank_swift"
                                    value={formData.bank_swift}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("bank_card")}
                                </label>
                                <input
                                    type="text"
                                    name="bank_card"
                                    value={formData.bank_card}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    BEP20
                                </label>
                                <input
                                    type="text"
                                    name="bep20"
                                    value={formData.bep20}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    TRC20
                                </label>
                                <input
                                    type="text"
                                    name="trc20"
                                    value={formData.trc20}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("member_fee_time")}
                                </label>
                                <input
                                    type="datetime-local"
                                    name="member_fee_time"
                                    value={formData.member_fee_time}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {t("member_fee_time_hint")}
                                </p>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("inviter")}
                                </label>
                                <div className="flex flex-col gap-2">
                                    <UserAutocomplete
                                        value={inviterInput}
                                        onChange={(value) => {
                                            setInviterInput(value);
                                            setFormData((prev) => ({
                                                ...prev,
                                                inviter_id: null,
                                                inviter_name: value,
                                            }));
                                        }}
                                        onUserSelect={(user: AutocompleteUser) => {
                                            setInviterInput(
                                                `${user.name} ${user.surname} (ID: ${user.id})`
                                            );
                                            setFormData((prev) => ({
                                                ...prev,
                                                inviter_id: user.id,
                                                inviter_name: `${user.name} ${user.surname} (ID: ${user.id})`,
                                            }));
                                        }}
                                        placeholder={t("inviter_placeholder")}
                                    />
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                        {formData.inviter_id
                                            ? `${t("current_inviter")}: ${formData.inviter_name || formData.inviter_id}`
                                            : t("inviter_not_set")}
                                    </div>
                                </div>
                            </div>

                            <div className="md:col-span-2 border-t pt-4 mt-2">
                                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                                    {tCommon("common.heir_info")}
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {tCommon("common.heir_name")}
                                        </label>
                                        <input
                                            type="text"
                                            name="heir_name"
                                            value={formData.heir_name}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {tCommon("common.heir_surname")}
                                        </label>
                                        <input
                                            type="text"
                                            name="heir_surname"
                                            value={formData.heir_surname}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {t("heir_passport")}
                                        </label>
                                        <input
                                            type="text"
                                            name="heir_passport"
                                            value={formData.heir_passport}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {t("heir_dob")}
                                        </label>
                                        <input
                                            type="date"
                                            name="heir_dob"
                                            value={formData.heir_dob}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("user_credit_history")}
                                </label>
                                <select
                                    name="user_credit_history"
                                    value={formData.user_credit_history}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="0">{t("credit_history_ok")}</option>
                                    <option value="1">{t("credit_history_bad")}</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Статус доступа
                                </label>
                                {(() => {
                                    const isInStructure = userData &&
                                        (userData as any).user_tree?.path &&
                                        Array.isArray((userData as any).user_tree.path) &&
                                        (userData as any).user_tree.path.length > 0;

                                    return (
                                        <>
                                            <select
                                                name="access_status"
                                                value={formData.access_status}
                                                onChange={handleInputChange}
                                                disabled={!isInStructure}
                                                className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                                                    !isInStructure ? 'opacity-50 cursor-not-allowed' : ''
                                                }`}
                                            >
                                                <option value="0">Активный</option>
                                                <option value="1">Заблокирован</option>
                                                <option value="2" disabled>Ограниченный доступ</option>
                                                <option value="3" disabled>Удаленный</option>
                                            </select>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                {isInStructure
                                                    ? "✓ Пользователь присутствует в структуре"
                                                    : "✗ Пользователь отсутствует в структуре. Изменение статуса доступа недоступно."}
                                            </p>
                                        </>
                                    );
                                })()}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Разрешить смену Telegram ID
                                </label>
                                <div className="flex items-center space-x-2 mt-1">
                                    <input
                                        type="checkbox"
                                        id="can_change_telegram"
                                        name="can_change_telegram"
                                        checked={formData.can_change_telegram}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                can_change_telegram: e.target.checked,
                                            }))
                                        }
                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                    />
                                    <label
                                        htmlFor="can_change_telegram"
                                        className="text-sm text-gray-700 dark:text-gray-300"
                                    >
                                        Разрешить пользователю смену Telegram ID
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Демо режим
                                </label>
                                <div className="flex items-center space-x-2 mt-1">
                                    <input
                                        type="checkbox"
                                        id="is_demo"
                                        name="is_demo"
                                        checked={formData.is_demo}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                is_demo: e.target.checked,
                                            }))
                                        }
                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                    />
                                    <label
                                        htmlFor="is_demo"
                                        className="text-sm text-gray-700 dark:text-gray-300"
                                    >
                                        Включить демо-режим для этого пользователя
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 pt-4 border-t">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={isSaving}
                                className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:bg-gray-900 disabled:opacity-50"
                            >
                                {tCommon("cancel")}
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isSaving ? (
                                    <div className="flex items-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        {tCommon("saving")}
                                    </div>
                                ) : (
                                    tCommon("save")
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default UserEditModal;

